/**
 * WarehouseSimulation — Heatmap, KPIs, Picker-Routen, Co-Pick Matrix, AI (placeholder).
 *
 * @page /warehouse/simulation
 * @roles admin, manager
 * @api GET /api/warehouse/:id/simulation/heatmap
 * @api GET /api/warehouse/:id/simulation/kpis
 * @api GET /api/warehouse/:id/simulation/routen
 * @api GET /api/warehouse/:id/simulation/copick
 * @api GET /api/warehouse/:id/config
 * @api GET /api/warehouse/:id/objekte
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Activity, Info, Sparkles, ArrowRight, ChevronDown, ChevronUp, CheckCircle2, XCircle, Clock, MapPin, Package, TrendingUp, BarChart3, Layers, Target, Gauge } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import useTabParam from '../hooks/useTabParam';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { getTodayVienna } from '../utils/dateUtils';

const API = import.meta.env.VITE_API || '';

async function apiFetch(url) {
  const res = await fetch(`${API}${url}`, { credentials: 'include' });
  return res.json();
}

// ─── Heatmap color scale ───
function getHeatColor(intensitaet) {
  if (intensitaet < 0.25) return 'rgba(51, 142, 220, 0.35)';
  if (intensitaet < 0.5) return 'rgba(34, 211, 238, 0.45)';
  if (intensitaet < 0.75) return 'rgba(245, 158, 11, 0.55)';
  return 'rgba(239, 68, 68, 0.65)';
}

function getHeatLabel(intensitaet, t) {
  if (intensitaet >= 0.75) return t('simulation.hotspot');
  if (intensitaet <= 0.25 && intensitaet > 0) return t('simulation.coldspot');
  return '';
}

// ─── KPI Card ───
function KpiCard({ label, value, sub, icon: Icon, iconColor }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 flex-1 min-w-[140px]">
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon size={16} className={iconColor || 'text-[#338edc]'} />}
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{label}</div>
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Tab Button ───
function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-1.5 text-sm font-medium rounded-[8px] transition-all whitespace-nowrap
        ${active ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
      {children}
    </button>
  );
}

// ─── Simulation Time Filter (unified across tabs) ───
function SimulationTimeFilter({ preset, von, bis, onChange, t }) {
  const isEn = t('simulation.zeitraum') === 'Period';
  const presets = [
    { key: 'heute', label: t('simulation.heute') },
    { key: 7, label: `7 ${isEn ? 'days' : 'Tage'}` },
    { key: 30, label: `30 ${isEn ? 'days' : 'Tage'}` },
    { key: 90, label: `90 ${isEn ? 'days' : 'Tage'}` },
    { key: 'custom', label: t('simulation.benutzerdefiniert') },
  ];

  function handlePreset(key) {
    const today = getTodayVienna();
    if (key === 'heute') {
      onChange({ preset: 'heute', von: today, bis: today });
    } else if (key === 'custom') {
      onChange({ preset: 'custom', von, bis });
    } else {
      const d = new Date(today + 'T12:00:00');
      d.setDate(d.getDate() - key);
      const vonStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      onChange({ preset: key, von: vonStr, bis: today });
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] inline-flex">
        {presets.map(p => (
          <button key={p.key} onClick={() => handlePreset(p.key)}
            className={`px-3 py-1 text-xs font-medium rounded-[8px] transition-all whitespace-nowrap
              ${preset === p.key ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-[var(--text-tertiary)]">{t('simulation.von')}:</span>
          <input type="date" value={von} onChange={e => onChange({ preset: 'custom', von: e.target.value, bis })}
            className="bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
          <span className="text-[var(--text-tertiary)]">{t('simulation.bis')}:</span>
          <input type="date" value={bis} onChange={e => onChange({ preset: 'custom', von, bis: e.target.value })}
            className="bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
        </div>
      )}
    </div>
  );
}

// ─── Placeholder Tab ───
function PlatzhalterTab({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-12 h-12 rounded-2xl bg-[#338edc]/10 flex items-center justify-center mb-3">
        <Activity className="w-6 h-6 text-[#338edc]" />
      </div>
      <p className="text-sm text-[var(--text-secondary)]">{text}</p>
    </div>
  );
}

// ═════════════════════════════════════════════
// HEATMAP SVG — Read-only floor plan with heat overlay
// ═════════════════════════════════════════════
function HeatmapFloorplan({ width, height, shelves, objekte, backgroundImage, bgOpacity, hoveredId, onHover, onLeave }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const scale = 40;
  const svgW = width * scale;
  const svgH = height * scale;

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.3, Math.min(5, prev * delta)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning) {
      setPan({ x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y });
    }
  }, [isPanning]);

  const gridLines = useMemo(() => {
    const lines = [];
    for (let x = 0; x <= width; x += 1) {
      const isMajor = x % 5 === 0;
      lines.push(<line key={`v${x}`} x1={x * scale} y1={0} x2={x * scale} y2={svgH}
        stroke="var(--border-card)" strokeWidth={isMajor ? 0.8 : 0.3} opacity={isMajor ? 0.5 : 0.25} />);
    }
    for (let y = 0; y <= height; y += 1) {
      const isMajor = y % 5 === 0;
      lines.push(<line key={`h${y}`} x1={0} y1={y * scale} x2={svgW} y2={y * scale}
        stroke="var(--border-card)" strokeWidth={isMajor ? 0.8 : 0.3} opacity={isMajor ? 0.5 : 0.25} />);
    }
    for (let x = 0; x <= width; x += 5) {
      lines.push(<text key={`xl${x}`} x={x * scale} y={svgH + 14} fill="var(--text-tertiary)" fontSize={9} textAnchor="middle">{x}m</text>);
    }
    for (let y = 0; y <= height; y += 5) {
      lines.push(<text key={`yl${y}`} x={-6} y={y * scale + 3} fill="var(--text-tertiary)" fontSize={9} textAnchor="end">{height - y}m</text>);
    }
    return lines;
  }, [width, height, scale, svgW, svgH]);

  // Objekt type definitions (mirrors WarehouseConfig)
  const OBJEKT_TYPEN = {
    packtisch: { color: '#22D3EE', darstellung: 'solid' },
    dock: { color: '#6366F1', darstellung: 'solid' },
    zone_anlieferung: { color: '#10B981', darstellung: 'gestrichelt' },
    zone_versand: { color: '#F59E0B', darstellung: 'gestrichelt' },
    buero: { color: '#9CA3AF', darstellung: 'solid' },
    tor: { color: '#6366F1', darstellung: 'solid' },
    gefahrgut: { color: '#EF4444', darstellung: 'gestrichelt' },
  };

  return (
    <div ref={containerRef}
      className="bg-[#0f172a] rounded-2xl border border-[var(--border-card)] overflow-hidden relative"
      style={{ height: '500px', cursor: isPanning ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={() => setIsPanning(false)}
      onMouseLeave={() => setIsPanning(false)}
    >
      <svg ref={svgRef} width="100%" height="100%" viewBox={`-20 -5 ${svgW + 40} ${svgH + 25}`}
        preserveAspectRatio="xMidYMid meet" className="select-none"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center', transition: isPanning ? 'none' : 'transform 0.1s ease-out' }}
      >
        {backgroundImage && <image href={backgroundImage} x={0} y={0} width={svgW} height={svgH} opacity={bgOpacity} preserveAspectRatio="xMidYMid slice" />}
        {gridLines}
        <rect x={0} y={0} width={svgW} height={svgH} fill="none" stroke="#475569" strokeWidth={2} rx={4} />

        {/* Render objects (non-shelf) */}
        {(objekte || []).map(obj => {
          const def = OBJEKT_TYPEN[obj.typ];
          if (!def) return null;
          const color = obj.farbe || def.color;
          const w = (obj.laenge_m || 2) * scale;
          const h = (obj.breite_m || 1) * scale;
          const x = (obj.pos_x || 0) * scale;
          const y = (obj.pos_y || 0) * scale;
          return (
            <g key={`obj-${obj.id}`}>
              <rect x={x} y={y} width={w} height={h}
                fill={color} fillOpacity={0.08} stroke={color} strokeWidth={1}
                strokeDasharray={def.darstellung === 'gestrichelt' ? '6,3' : 'none'} rx={3} />
              <text x={x + w / 2} y={y + h / 2 + 3} fill={color} fontSize={8} fontWeight={500}
                textAnchor="middle" opacity={0.7}>{obj.name}</text>
            </g>
          );
        })}

        {/* Render shelves with heat colors */}
        {shelves.map(shelf => {
          const rot = shelf.rotation || 0;
          const sw = (rot === 90 || rot === 270) ? shelf.breite_m : shelf.laenge_m;
          const sh = (rot === 90 || rot === 270) ? shelf.laenge_m : shelf.breite_m;
          const heatColor = shelf.zugriffe > 0 ? getHeatColor(shelf.intensitaet) : (shelf.color || 'rgba(100,116,139,0.15)');
          const isHovered = hoveredId === shelf.regal_id;

          // Rack lines for fachboden/hochregal
          const rackLines = [];
          if (shelf.shelf_type === 'fachboden' || shelf.shelf_type === 'hochregal') {
            const rows = 5;
            const spacing = sh / (rows + 1);
            for (let i = 1; i <= rows; i++) {
              rackLines.push(<rect key={i} x={(shelf.pos_x + 0.3) * scale} y={(shelf.pos_y + i * spacing - 0.1) * scale}
                width={(sw - 0.6) * scale} height={1.5} fill={shelf.zugriffe > 0 ? heatColor : (shelf.color || '#64748b')} opacity={0.4} rx={1} />);
            }
          }

          return (
            <g key={shelf.regal_id}
              onMouseEnter={() => onHover(shelf.regal_id)}
              onMouseLeave={onLeave}
              className="cursor-pointer"
            >
              <rect x={shelf.pos_x * scale} y={shelf.pos_y * scale}
                width={sw * scale} height={sh * scale}
                fill={shelf.zugriffe > 0 ? heatColor : (shelf.color || '#64748b')}
                fillOpacity={shelf.zugriffe > 0 ? 1 : 0.12}
                stroke={isHovered ? '#fff' : (shelf.zugriffe > 0 ? heatColor : (shelf.color || '#64748b'))}
                strokeWidth={isHovered ? 2.5 : 1.2} rx={3} />
              {rackLines}
              <text x={(shelf.pos_x + sw / 2) * scale} y={(shelf.pos_y + sh / 2 + 0.3) * scale}
                fill={shelf.zugriffe > 0 ? '#fff' : (shelf.color || '#94a3b8')}
                fontSize={Math.min(11, sw * scale / 6)} fontWeight={600} textAnchor="middle"
                fontFamily="inherit" opacity={0.9}>
                {shelf.name}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}


// ═════════════════════════════════════════════
// HEATMAP TAB
// ═════════════════════════════════════════════
function HeatmapTab({ t, von, bis }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const warehouseId = 1;

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [hm, cfg, obj] = await Promise.all([
        apiFetch(`/api/warehouse/${warehouseId}/simulation/heatmap?von=${von}&bis=${bis}`),
        apiFetch(`/api/warehouse/${warehouseId}/config`),
        apiFetch(`/api/warehouse/${warehouseId}/objekte`),
      ]);
      if (hm && !hm.error) setData(hm);
      if (cfg && !cfg.error) setConfig(cfg);
      if (Array.isArray(obj)) setObjekte(obj);
      setLoading(false);
    }
    load();
  }, [von, bis]);

  const hoveredShelf = data?.regale?.find(r => r.regal_id === hoveredId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const halleWidth = config?.width || 30;
  const halleHeight = config?.height || 20;
  const bgImage = config?.background_image ? `${API}/api/warehouse/${warehouseId}/config/background?t=${Date.now()}` : null;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-end">
        <span className="text-sm text-[var(--text-secondary)]">
          {(data?.gesamt_zugriffe || 0).toLocaleString()} {t('simulation.zugriffe')}
        </span>
      </div>

      {/* Floor plan with heatmap overlay */}
      <div className="relative">
        <HeatmapFloorplan
          width={halleWidth}
          height={halleHeight}
          shelves={data?.regale || []}
          objekte={objekte}
          backgroundImage={bgImage}
          bgOpacity={0.15}
          hoveredId={hoveredId}
          onHover={setHoveredId}
          onLeave={() => setHoveredId(null)}
        />

        {/* Tooltip */}
        {hoveredShelf && (
          <div className="absolute top-3 right-3 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl px-4 py-3 shadow-sm z-10 min-w-[200px]">
            <div className="text-sm font-semibold text-[var(--text-primary)] mb-1">{hoveredShelf.name}</div>
            <div className="text-xs text-[var(--text-secondary)] space-y-0.5">
              <div>{hoveredShelf.zugriffe.toLocaleString()} {t('simulation.zugriffe')} ({'\u00D8'} {hoveredShelf.zugriffe_pro_tag}/{t('simulation.zugriffeProTag') === 'per day' ? 'day' : 'Tag'})</div>
              {hoveredShelf.intensitaet >= 0.75 && (
                <div className="text-red-500 font-medium">{t('simulation.hotspot')}</div>
              )}
              {hoveredShelf.intensitaet > 0 && hoveredShelf.intensitaet <= 0.25 && (
                <div className="text-blue-400 font-medium">{t('simulation.coldspot')}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Color scale legend */}
      <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
        <span>{t('simulation.farbskala').split('───')[0] || 'Few'}</span>
        <div className="flex-1 h-2 rounded-full" style={{
          background: 'linear-gradient(to right, rgba(51,142,220,0.4), rgba(34,211,238,0.5), rgba(245,158,11,0.6), rgba(239,68,68,0.7))'
        }} />
        <span>{t('simulation.farbskala').split('───')[1] || 'Many'}</span>
      </div>

      {/* Top shelves list */}
      {data?.regale?.some(r => r.zugriffe > 0) && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">
            {t('simulation.topRegale')}
          </h3>
          <div className="space-y-2">
            {data.regale.filter(r => r.zugriffe > 0).slice(0, 5).map((r, i) => {
              const pct = data.gesamt_zugriffe > 0 ? Math.round(r.zugriffe / data.gesamt_zugriffe * 1000) / 10 : 0;
              return (
                <div key={r.regal_id} className="flex items-center gap-3">
                  <span className="text-xs text-[var(--text-tertiary)] w-5 text-right">{i + 1}.</span>
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getHeatColor(r.intensitaet).replace(/[\d.]+\)$/, '1)') }} />
                  <span className="text-sm font-medium text-[var(--text-primary)] w-24">{r.name}</span>
                  <div className="flex-1 h-1.5 bg-[var(--bg-inset)] rounded-full overflow-hidden">
                    <div className="h-full bg-[#338edc] rounded-full" style={{ width: `${pct * 5}%` }} />
                  </div>
                  <span className="text-xs text-[var(--text-secondary)] w-20 text-right">{r.zugriffe.toLocaleString()}</span>
                  <span className="text-xs text-[var(--text-tertiary)] w-12 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No data hint */}
      {(!data?.regale || data.regale.every(r => r.zugriffe === 0)) && (
        <div className="flex items-start gap-2 px-4 py-3 bg-[#338edc]/8 border border-[#338edc]/15 rounded-xl text-xs text-[var(--text-secondary)]">
          <Info className="w-4 h-4 text-[#338edc] shrink-0 mt-0.5" />
          <div>
            Shelves have no pick data yet. Ensure shelf prefixes (odoo_prefix) match location names from Odoo,
            or assign locations to shelves in the setup page.
          </div>
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════
// KPIs TAB
// ═════════════════════════════════════════════
function KpisTab({ t, von, bis }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const warehouseId = 1;

  useEffect(() => {
    async function load() {
      setLoading(true);
      const kpis = await apiFetch(`/api/warehouse/${warehouseId}/simulation/kpis?von=${von}&bis=${bis}`);
      if (kpis && !kpis.error) setData(kpis);
      setLoading(false);
    }
    load();
  }, [von, bis]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const trendData = (data.zugriffe_trend || []).map(d => ({
    datum: new Date(d.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }),
    zugriffe: d.zugriffe,
  }));

  const tage = Math.max(1, Math.round((new Date(bis) - new Date(von)) / 86400000) + 1);
  const isEn = t('simulation.zeitraum') === 'Period';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <span className="text-sm text-[var(--text-secondary)]">
          {(data.gesamt_zugriffe || 0).toLocaleString()} {t('simulation.zugriffe')}
        </span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          icon={BarChart3} iconColor="text-[#338edc]"
          label={t('simulation.zugriffe')}
          value={(data.gesamt_zugriffe || 0).toLocaleString()}
          sub={`${tage} ${isEn ? 'days' : 'Tage'}`}
        />
        <KpiCard
          icon={Target} iconColor="text-[#EF4444]"
          label={t('simulation.hotspots')}
          value={data.hotspot_count || 0}
          sub={`> 2x ${isEn ? 'avg' : '\u00D8'}`}
        />
        <KpiCard
          icon={Gauge} iconColor="text-[#22D3EE]"
          label={t('warehouse.auslastung')}
          value={`${data.auslastung_pct || 0}%`}
          sub={`${data.auslastung_regale?.hoch || 0}h / ${data.auslastung_regale?.mittel || 0}m / ${data.auslastung_regale?.niedrig || 0}l`}
        />
        <KpiCard
          icon={Layers} iconColor="text-[#6366F1]"
          label={t('simulation.gleichmaessig')}
          value={`${data.gleichmaessig_pct || 0}%`}
          sub="Gini"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Trend Chart */}
        <div className="col-span-2 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-4">
            {t('simulation.zugriffe')} / {isEn ? 'Day' : 'Tag'}
          </h3>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="zugriffeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#338edc" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#338edc" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card)" opacity={0.5} />
                <XAxis dataKey="datum" tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
                  axisLine={{ stroke: 'var(--border-card)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
                  axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-card)',
                    borderRadius: '10px',
                    fontSize: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                  labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                  itemStyle={{ color: 'var(--text-secondary)' }}
                />
                <Area type="monotone" dataKey="zugriffe" stroke="#338edc" strokeWidth={2}
                  fill="url(#zugriffeGrad)" dot={false} activeDot={{ r: 4, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-sm text-[var(--text-tertiary)]">
              No trend data available
            </div>
          )}
        </div>

        {/* Top / Bottom shelves */}
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">
            {t('simulation.topRegale')} / {t('simulation.bottomRegale')}
          </h3>
          <div className="space-y-1.5">
            {(data.top_regale || []).map((r, i) => (
              <div key={`t-${i}`} className="flex items-center gap-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-[var(--text-primary)] font-medium flex-1">{r.name}</span>
                <span className="text-[var(--text-secondary)] tabular-nums">{r.zugriffe.toLocaleString()}</span>
                <span className="text-[var(--text-tertiary)] w-10 text-right tabular-nums">{r.pct}%</span>
              </div>
            ))}
            {(data.top_regale?.length > 0 && data.bottom_regale?.length > 0) && (
              <div className="border-t border-[var(--border-card)] my-2" />
            )}
            {(data.bottom_regale || []).map((r, i) => (
              <div key={`b-${i}`} className="flex items-center gap-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[var(--text-primary)] font-medium flex-1">{r.name}</span>
                <span className="text-[var(--text-secondary)] tabular-nums">{r.zugriffe.toLocaleString()}</span>
                <span className="text-[var(--text-tertiary)] w-10 text-right tabular-nums">{r.pct}%</span>
              </div>
            ))}
            {(!data.top_regale?.length && !data.bottom_regale?.length) && (
              <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">No shelf data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Route colors ───
const ROUTE_COLORS = ['#338edc', '#6366F1', '#22D3EE', '#f59e0b', '#10b981', '#ec4899', '#ef4444', '#8b5cf6'];

// ═════════════════════════════════════════════
// PICKER-ROUTEN TAB
// ═════════════════════════════════════════════
function RoutenTab({ t, von, bis }) {
  const [routenModus, setRoutenModus] = useState('einzeltag');
  const [pickerId, setPickerId] = useState(null);
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWave, setSelectedWave] = useState(null);
  const warehouseId = 1;

  useEffect(() => {
    async function load() {
      setLoading(true);
      const pickerParam = pickerId ? `&picker_id=${pickerId}` : '';
      const url = routenModus === 'aggregiert'
        ? `/api/warehouse/${warehouseId}/simulation/routen?modus=aggregiert&von=${von}&bis=${bis}${pickerParam}`
        : `/api/warehouse/${warehouseId}/simulation/routen?datum=${von}${pickerParam}`;
      const [routes, cfg, obj] = await Promise.all([
        apiFetch(url),
        config ? Promise.resolve(config) : apiFetch(`/api/warehouse/${warehouseId}/config`),
        objekte.length > 0 ? Promise.resolve(objekte) : apiFetch(`/api/warehouse/${warehouseId}/objekte`),
      ]);
      if (routes && !routes.error) { setData(routes); setSelectedWave(null); }
      if (cfg && !cfg.error) setConfig(cfg);
      if (Array.isArray(obj)) setObjekte(obj);
      setLoading(false);
    }
    load();
  }, [von, bis, pickerId, routenModus]);

  const halleWidth = config?.width || 30;
  const halleHeight = config?.height || 20;
  const bgImage = config?.background_image ? `${API}/api/warehouse/${warehouseId}/config/background?t=${Date.now()}` : null;
  const scale = 40;
  const isEn = t('simulation.zeitraum') === 'Period';

  return (
    <div className="space-y-4">
      {/* Mode toggle + picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] inline-flex">
          <button onClick={() => setRoutenModus('einzeltag')}
            className={`px-3 py-1 text-xs font-medium rounded-[8px] transition-all whitespace-nowrap
              ${routenModus === 'einzeltag' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            {t('simulation.einzeltag')}
          </button>
          <button onClick={() => setRoutenModus('aggregiert')}
            className={`px-3 py-1 text-xs font-medium rounded-[8px] transition-all whitespace-nowrap
              ${routenModus === 'aggregiert' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            {t('simulation.aggregiert')}
          </button>
        </div>
        <div>
          <select value={pickerId || ''} onChange={e => setPickerId(e.target.value ? parseInt(e.target.value) : null)}
            className="bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] min-w-[200px]">
            <option value="">{t('simulation.picker')}: {isEn ? 'All' : 'Alle'}</option>
            {(data?.pickers || []).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
          <span><strong className="text-[var(--text-primary)]">{data?.gesamt_waves || 0}</strong> {t('simulation.waves')}</span>
          <span>{t('simulation.avgWegProWave')}: <strong className="text-[var(--text-primary)]">{data?.avg_weg_pro_wave || 0}m</strong></span>
          {routenModus === 'einzeltag' && (
            <span>{t('simulation.gesamtWeg')}: <strong className="text-[var(--text-primary)]">{data?.gesamt_weg_m || 0}m</strong></span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : routenModus === 'aggregiert' ? (
        /* ── Aggregiert ── */
        <div className="space-y-4">
          {/* KPI cards */}
          <div className="grid grid-cols-4 gap-4">
            <KpiCard icon={Package} iconColor="text-[#338edc]" label={t('simulation.waves')} value={data?.gesamt_waves || 0} sub={`${data?.tage_mit_daten || 0} ${t('simulation.tageMitDaten')}`} />
            <KpiCard icon={MapPin} iconColor="text-[#22D3EE]" label={t('simulation.avgWegProWave')} value={`${data?.avg_weg_pro_wave || 0}m`} />
            <KpiCard icon={TrendingUp} iconColor="text-[#10B981]" label={t('simulation.wavesProTag')} value={data?.avg_waves_pro_tag || 0} />
            <KpiCard icon={Activity} iconColor="text-[#6366F1]" label={t('simulation.gesamtWeg')} value={`${data?.gesamt_weg_m || 0}m`} />
          </div>

          {/* Daily trend table */}
          {(data?.taeglicher_trend || []).length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-card)]">
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('simulation.datum')}</th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('simulation.waves')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.taeglicher_trend.map(d => (
                    <tr key={d.datum} className="border-b border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-card)] last:border-0">
                      <td className="px-4 py-2 text-[var(--text-primary)] tabular-nums">
                        {new Date(d.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-2 text-right text-[var(--text-primary)] tabular-nums">{d.waves}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Top shelves */}
          {(data?.top_regale || []).length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">{t('simulation.topBesuchteRegale')}</h3>
              <div className="space-y-2">
                {data.top_regale.map((r, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-tertiary)] w-5 text-right">{i + 1}.</span>
                    <span className="text-sm font-medium text-[var(--text-primary)] w-24">{r.name}</span>
                    <div className="flex-1 h-1.5 bg-[var(--bg-inset)] rounded-full overflow-hidden">
                      <div className="h-full bg-[#338edc] rounded-full" style={{ width: `${Math.min(100, (r.besuche / (data.top_regale[0]?.besuche || 1)) * 100)}%` }} />
                    </div>
                    <span className="text-xs text-[var(--text-secondary)] tabular-nums">{r.besuche} {t('simulation.besuche')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── Einzeltag ── */
        <>
          {/* Floor plan with routes */}
          <div className="relative bg-[#0f172a] rounded-2xl border border-[var(--border-card)] overflow-hidden" style={{ height: '450px' }}>
            <RoutenFloorplan
              width={halleWidth} height={halleHeight} scale={scale}
              waves={data?.waves || []} startEnd={data?.start_end || { x: 15, y: 10 }}
              selectedWave={selectedWave} objekte={objekte}
              backgroundImage={bgImage} bgOpacity={0.15}
            />
          </div>

          {/* Wave list */}
          {(data?.waves || []).length > 0 ? (
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-card)]">
                    <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">#</th>
                    <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('simulation.datum')}</th>
                    <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('warehouse.kunden')}</th>
                    <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('warehouse.regale')}</th>
                    <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('simulation.route')}</th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('simulation.zeitraum')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.waves.map((w, i) => {
                    const isSelected = selectedWave === w.wave_id;
                    return (
                      <tr key={w.wave_id}
                        onClick={() => setSelectedWave(isSelected ? null : w.wave_id)}
                        className={`border-b border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-card)] last:border-0 cursor-pointer transition-colors
                          ${isSelected ? 'bg-[#338edc]/8' : 'hover:bg-[var(--bg-card-hover)]'}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ROUTE_COLORS[i % ROUTE_COLORS.length] }} />
                            <span className="text-[var(--text-primary)] font-medium">{i + 1}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-[var(--text-primary)] tabular-nums">{w.start_time}</td>
                        <td className="px-3 py-2.5 text-[var(--text-secondary)] text-xs max-w-[200px] truncate">
                          {w.kunden.slice(0, 3).join(', ')}{w.kunden.length > 3 && ` +${w.kunden.length - 3}`}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)]">
                          {w.regale.map(r => r.name).join(' \u2192 ') || '\u2014'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-[var(--text-primary)] tabular-nums">{w.geschaetzter_weg_m}m</td>
                        <td className="px-4 py-2.5 text-right text-[var(--text-secondary)] tabular-nums">
                          {w.dauer_min != null ? `${Math.round(w.dauer_min * 10) / 10}min` : '\u2014'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-start gap-2 px-4 py-3 bg-[#338edc]/8 border border-[#338edc]/15 rounded-xl text-xs text-[var(--text-secondary)]">
              <Info className="w-4 h-4 text-[#338edc] shrink-0 mt-0.5" />
              <div>No waves found for this picker and date. Try selecting a different date or picker.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Route SVG overlay inside the floor plan ───
function RoutenFloorplan({ width, height, scale, waves, startEnd, selectedWave, objekte, backgroundImage, bgOpacity }) {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const svgW = width * scale;
  const svgH = height * scale;

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(prev => Math.max(0.3, Math.min(5, prev * (e.deltaY > 0 ? 0.9 : 1.1))));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const gridLines = useMemo(() => {
    const lines = [];
    for (let x = 0; x <= width; x += 1) {
      const isMajor = x % 5 === 0;
      lines.push(<line key={`v${x}`} x1={x * scale} y1={0} x2={x * scale} y2={svgH}
        stroke="var(--border-card)" strokeWidth={isMajor ? 0.8 : 0.3} opacity={isMajor ? 0.5 : 0.25} />);
    }
    for (let y = 0; y <= height; y += 1) {
      const isMajor = y % 5 === 0;
      lines.push(<line key={`h${y}`} x1={0} y1={y * scale} x2={svgW} y2={y * scale}
        stroke="var(--border-card)" strokeWidth={isMajor ? 0.8 : 0.3} opacity={isMajor ? 0.5 : 0.25} />);
    }
    return lines;
  }, [width, height, scale, svgW, svgH]);

  // Collect all unique shelves across all waves
  const allShelves = {};
  for (const w of waves) {
    for (const r of w.regale) { allShelves[r.regal_id] = r; }
  }

  return (
    <div ref={containerRef} className="w-full h-full" style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
      onMouseDown={e => { if (e.button === 0) { setIsPanning(true); panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; } }}
      onMouseMove={e => { if (isPanning) setPan({ x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y }); }}
      onMouseUp={() => setIsPanning(false)} onMouseLeave={() => setIsPanning(false)}>
      <svg width="100%" height="100%" viewBox={`-20 -5 ${svgW + 40} ${svgH + 25}`}
        preserveAspectRatio="xMidYMid meet" className="select-none"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center', transition: isPanning ? 'none' : 'transform 0.1s ease-out' }}>
        {backgroundImage && <image href={backgroundImage} x={0} y={0} width={svgW} height={svgH} opacity={bgOpacity} preserveAspectRatio="xMidYMid slice" />}
        {gridLines}
        <rect x={0} y={0} width={svgW} height={svgH} fill="none" stroke="#475569" strokeWidth={2} rx={4} />

        {/* Shelves as faint rectangles */}
        {Object.values(allShelves).map(shelf => (
          <rect key={shelf.regal_id} x={shelf.pos_x * scale} y={shelf.pos_y * scale}
            width={2 * scale} height={0.6 * scale}
            fill="#64748b" fillOpacity={0.15} stroke="#64748b" strokeWidth={1} rx={3} />
        ))}

        {/* Route polylines */}
        {waves.map((w, i) => {
          const isActive = selectedWave === w.wave_id;
          const showLine = selectedWave === null || isActive;
          if (!showLine && selectedWave !== null) return null;
          const points = [startEnd, ...w.regale.map(r => ({ x: r.pos_x, y: r.pos_y })), startEnd];
          const polyPoints = points.map(p => `${(p.x + 1) * scale},${(p.y + 0.3) * scale}`).join(' ');
          return (
            <polyline key={w.wave_id} points={polyPoints} fill="none"
              stroke={ROUTE_COLORS[i % ROUTE_COLORS.length]}
              strokeWidth={isActive ? 3 : 1.5}
              strokeDasharray={isActive ? 'none' : '6 3'}
              opacity={isActive ? 1 : (selectedWave === null ? 0.4 : 0.1)}
              strokeLinejoin="round" strokeLinecap="round" />
          );
        })}

        {/* Shelf name labels for visited shelves */}
        {Object.values(allShelves).map(shelf => (
          <text key={`lbl-${shelf.regal_id}`}
            x={(shelf.pos_x + 1) * scale} y={(shelf.pos_y - 0.2) * scale}
            fill="#94a3b8" fontSize={9} fontWeight={600} textAnchor="middle">
            {shelf.name}
          </text>
        ))}

        {/* Start/End marker */}
        <circle cx={(startEnd.x + 1) * scale} cy={(startEnd.y + 0.3) * scale} r={5}
          fill="#10b981" stroke="#fff" strokeWidth={2} />
        <text x={(startEnd.x + 1) * scale} y={(startEnd.y + 0.3) * scale + 14}
          fill="#10b981" fontSize={8} fontWeight={600} textAnchor="middle">START</text>
      </svg>
    </div>
  );
}


// ═════════════════════════════════════════════
// CO-PICK MATRIX TAB
// ═════════════════════════════════════════════
function CopickTab({ t, von, bis }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPair, setSelectedPair] = useState(null);
  const warehouseId = 1;

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [cp, cfg] = await Promise.all([
        apiFetch(`/api/warehouse/${warehouseId}/simulation/copick?von=${von}&bis=${bis}`),
        config ? Promise.resolve(config) : apiFetch(`/api/warehouse/${warehouseId}/config`),
      ]);
      if (cp && !cp.error) setData(cp);
      if (cfg && !cfg.error) setConfig(cfg);
      setLoading(false);
    }
    load();
  }, [von, bis]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  function distanceStatus(m) {
    if (m === null) return { icon: '—', color: 'text-[var(--text-tertiary)]', label: '' };
    if (m < 15) return { icon: '\u2705', color: 'text-emerald-500', label: t('simulation.abstandOk') };
    if (m < 30) return { icon: '\u26A0\uFE0F', color: 'text-amber-500', label: t('simulation.abstandSuboptimal') };
    return { icon: '\uD83D\uDD34', color: 'text-red-500', label: t('simulation.abstandSchlecht') };
  }

  const paare = data?.paare || [];
  const maxWaves = paare.length > 0 ? Math.max(...paare.map(p => p.gemeinsame_waves)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <span className="text-sm text-[var(--text-secondary)]">
          {paare.length} {t('simulation.copickPaare')}
        </span>
      </div>

      {/* Pair cards */}
      <div className="space-y-3">
        {paare.map((pair, i) => {
          const ds = distanceStatus(pair.abstand_m);
          const isSelected = selectedPair === i;
          return (
            <div key={i}
              onClick={() => setSelectedPair(isSelected ? null : i)}
              className={`bg-[var(--bg-card)] border rounded-2xl p-4 cursor-pointer transition-all
                ${isSelected ? 'border-[#338edc] ring-1 ring-[#338edc]/20' : 'border-[var(--border-card)] hover:border-[#338edc]/30'}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">{pair.kunde_a.name}</span>
                    <span className="text-xs text-[var(--text-tertiary)]">+</span>
                    <span className="text-sm font-semibold text-[var(--text-primary)]">{pair.kunde_b.name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
                    <span>{t('simulation.quote')}: <strong className="text-[var(--text-primary)]">{pair.quote_pct}%</strong></span>
                    <span>{t('simulation.gemeinsameWaves')}: <strong className="text-[var(--text-primary)]">{pair.gemeinsame_waves}</strong></span>
                    {pair.abstand_m != null && (
                      <span>{t('simulation.abstand')}: <strong className="text-[var(--text-primary)]">{pair.abstand_m}m</strong></span>
                    )}
                  </div>
                  {/* Quote bar */}
                  <div className="mt-2 w-full h-1.5 bg-[var(--bg-inset)] rounded-full overflow-hidden">
                    <div className="h-full bg-[#338edc] rounded-full transition-all"
                      style={{ width: `${(pair.gemeinsame_waves / maxWaves) * 100}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {pair.abstand_m != null ? (
                    <div className={`text-xs font-medium ${ds.color}`}>{ds.label}</div>
                  ) : (
                    <div className="text-xs text-[var(--text-tertiary)]">{t('simulation.abstand')}: —</div>
                  )}
                </div>
              </div>

              {/* Expanded: show shelves */}
              {isSelected && (pair.regale_a.length > 0 || pair.regale_b.length > 0) && (
                <div className="mt-3 pt-3 border-t border-[var(--border-card)] flex gap-6 text-xs">
                  <div>
                    <div className="text-[var(--text-tertiary)] mb-1">{pair.kunde_a.name}</div>
                    <div className="flex gap-1 flex-wrap">
                      {pair.regale_a.map((r, j) => (
                        <span key={j} className="px-2 py-0.5 bg-[#338edc]/10 text-[#338edc] rounded font-medium">{r}</span>
                      ))}
                      {pair.regale_a.length === 0 && <span className="text-[var(--text-tertiary)]">No shelf assigned</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[var(--text-tertiary)] mb-1">{pair.kunde_b.name}</div>
                    <div className="flex gap-1 flex-wrap">
                      {pair.regale_b.map((r, j) => (
                        <span key={j} className="px-2 py-0.5 bg-[#6366F1]/10 text-[#6366F1] rounded font-medium">{r}</span>
                      ))}
                      {pair.regale_b.length === 0 && <span className="text-[var(--text-tertiary)]">No shelf assigned</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded: floor plan view with both shelf clusters */}
              {isSelected && pair.pos_a && pair.pos_b && (
                <div className="mt-3 bg-[#0f172a] rounded-xl overflow-hidden" style={{ height: '200px' }}>
                  <CopickMiniMap pair={pair} config={config} />
                </div>
              )}
            </div>
          );
        })}

        {paare.length === 0 && (
          <div className="flex items-start gap-2 px-4 py-3 bg-[#338edc]/8 border border-[#338edc]/15 rounded-xl text-xs text-[var(--text-secondary)]">
            <Info className="w-4 h-4 text-[#338edc] shrink-0 mt-0.5" />
            <div>No co-pick pairs found with at least 5 shared waves in the selected period.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mini floor plan for co-pick pair ───
function CopickMiniMap({ pair, config }) {
  const w = config?.width || 30;
  const h = config?.height || 20;
  const scale = 30;
  const svgW = w * scale;
  const svgH = h * scale;

  return (
    <svg width="100%" height="100%" viewBox={`-10 -5 ${svgW + 20} ${svgH + 10}`}
      preserveAspectRatio="xMidYMid meet" className="select-none">
      <rect x={0} y={0} width={svgW} height={svgH} fill="none" stroke="#475569" strokeWidth={1} rx={3} />

      {/* Position A */}
      <circle cx={pair.pos_a.x * scale} cy={pair.pos_a.y * scale} r={8}
        fill="#338edc" fillOpacity={0.3} stroke="#338edc" strokeWidth={2} />
      <text x={pair.pos_a.x * scale} y={pair.pos_a.y * scale - 12}
        fill="#338edc" fontSize={9} fontWeight={600} textAnchor="middle">
        {pair.kunde_a.name.split(' ')[0]}
      </text>

      {/* Position B */}
      <circle cx={pair.pos_b.x * scale} cy={pair.pos_b.y * scale} r={8}
        fill="#6366F1" fillOpacity={0.3} stroke="#6366F1" strokeWidth={2} />
      <text x={pair.pos_b.x * scale} y={pair.pos_b.y * scale - 12}
        fill="#6366F1" fontSize={9} fontWeight={600} textAnchor="middle">
        {pair.kunde_b.name.split(' ')[0]}
      </text>

      {/* Connection line */}
      <line x1={pair.pos_a.x * scale} y1={pair.pos_a.y * scale}
        x2={pair.pos_b.x * scale} y2={pair.pos_b.y * scale}
        stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />

      {/* Distance label at midpoint */}
      {pair.abstand_m != null && (
        <text
          x={(pair.pos_a.x + pair.pos_b.x) / 2 * scale}
          y={(pair.pos_a.y + pair.pos_b.y) / 2 * scale - 6}
          fill="#f59e0b" fontSize={10} fontWeight={600} textAnchor="middle">
          {pair.abstand_m}m
        </text>
      )}
    </svg>
  );
}


// ═════════════════════════════════════════════
// AI-OPTIMIERUNG TAB
// ═════════════════════════════════════════════
function AiOptimierungTab({ t }) {
  const [suggestions, setSuggestions] = useState([]);
  const [problemLines, setProblemLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showFloorplan, setShowFloorplan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const warehouseId = 1;

  const loadData = useCallback(async () => {
    setLoading(true);
    const url = `/api/warehouse/${warehouseId}/suggestions${statusFilter ? `?status=${statusFilter}` : ''}`;
    const [data, cfg] = await Promise.all([
      apiFetch(url),
      config ? Promise.resolve(config) : apiFetch(`/api/warehouse/${warehouseId}/config`),
    ]);
    if (data && !data.error) {
      setSuggestions(data.suggestions || []);
      setProblemLines(data.problem_lines || []);
    }
    if (cfg && !cfg.error) setConfig(cfg);
    setLoading(false);
  }, [statusFilter, config]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/warehouse/${warehouseId}/suggestions/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || t('simulation.aiFehler'));
      } else {
        await loadData();
      }
    } catch {
      setError(t('simulation.aiFehler'));
    }
    setGenerating(false);
  }

  async function handleStatusChange(id, status) {
    await fetch(`${API}/api/warehouse/${warehouseId}/suggestions/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    await loadData();
  }

  const PRIO_COLORS = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };
  const PRIO_LABELS = { high: t('simulation.aiHoch'), medium: t('simulation.aiMittel'), low: t('simulation.aiNiedrig') };

  const STATUS_ICONS = {
    pending: <Clock className="w-3.5 h-3.5" />,
    accepted: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
    rejected: <XCircle className="w-3.5 h-3.5 text-red-400" />,
    implemented: <CheckCircle2 className="w-3.5 h-3.5 text-[#338edc]" />,
  };
  const STATUS_LABELS = {
    pending: t('simulation.aiPending'),
    accepted: t('simulation.aiAccepted'),
    rejected: t('simulation.aiRejected'),
    implemented: t('simulation.aiImplemented'),
  };

  // Summary calculations
  const pendingOrAccepted = suggestions.filter(s => s.status === 'pending' || s.status === 'accepted');
  const totalSavingsM = pendingOrAccepted.reduce((s, v) => s + (v.expected_saving_meters || 0), 0);
  const totalPicks = pendingOrAccepted.reduce((s, v) => s + (v.affected_picks_month || 0), 0);
  const highCount = suggestions.filter(s => s.priority === 'high').length;
  const mediumCount = suggestions.filter(s => s.priority === 'medium').length;
  const lowCount = suggestions.filter(s => s.priority === 'low').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Status filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
            <option value="">{t('simulation.aiStatus')}: Alle</option>
            <option value="pending">{STATUS_LABELS.pending}</option>
            <option value="accepted">{STATUS_LABELS.accepted}</option>
            <option value="rejected">{STATUS_LABELS.rejected}</option>
            <option value="implemented">{STATUS_LABELS.implemented}</option>
          </select>
          <span className="text-sm text-[var(--text-secondary)]">
            {suggestions.length} {t('simulation.aiAnzahl')}
          </span>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-[#338edc] text-white rounded-xl text-sm font-medium hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
          {generating ? (
            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {generating ? t('simulation.aiLaden') : t('simulation.aiGenerieren')}
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Summary card */}
      {suggestions.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard
            icon={Sparkles} iconColor="text-[#338edc]"
            label={t('simulation.aiGesamt')}
            value={`${Math.round(totalSavingsM)}m`}
            sub={`${pendingOrAccepted.length} ${t('simulation.aiAnzahl')}`}
          />
          <KpiCard
            icon={Package} iconColor="text-[#22D3EE]"
            label={t('simulation.aiBetroffenePicksGesamt')}
            value={totalPicks.toLocaleString()}
            sub={`${t('simulation.aiPicksMonat')}`}
          />
          <KpiCard
            icon={Activity} iconColor="text-[#F59E0B]"
            label={t('simulation.aiPrioritaet')}
            value={`${highCount}h / ${mediumCount}m / ${lowCount}l`}
            sub={`${suggestions.length} total`}
          />
          <KpiCard
            icon={CheckCircle2} iconColor="text-[#10B981]"
            label={t('simulation.aiStatus')}
            value={`${suggestions.filter(s => s.status === 'implemented').length}`}
            sub={`${t('simulation.aiImplemented')}`}
          />
        </div>
      )}

      {/* Suggestion cards */}
      {suggestions.length === 0 && !generating ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-12 h-12 rounded-2xl bg-[#338edc]/10 flex items-center justify-center mb-3">
            <Sparkles className="w-6 h-6 text-[#338edc]" />
          </div>
          <p className="text-sm text-[var(--text-secondary)] max-w-md text-center">{t('simulation.aiKeine')}</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('simulation.aiGenerierenSub')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s, i) => {
            const isExpanded = expanded === s.id;
            const prioClass = PRIO_COLORS[s.priority] || PRIO_COLORS.medium;
            const isActive = s.status === 'pending' || s.status === 'accepted';
            return (
              <div key={s.id}
                className={`bg-[var(--bg-card)] border rounded-2xl overflow-hidden transition-all
                  ${isActive ? 'border-[var(--border-card)]' : 'border-[var(--border-card)] opacity-70'}`}>
                {/* Header */}
                <div className="p-4 cursor-pointer flex items-start gap-4" onClick={() => setExpanded(isExpanded ? null : s.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold ${prioClass}`}>
                        {PRIO_LABELS[s.priority] || s.priority}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
                        {STATUS_ICONS[s.status]} {STATUS_LABELS[s.status]}
                      </span>
                      {s.accepted_by_name && (
                        <span className="text-[11px] text-[var(--text-tertiary)]">— {s.accepted_by_name}</span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--text-primary)]">{s.reason}</div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-[var(--text-secondary)]">
                      {s.from_location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {s.from_location}
                          {s.to_location && (
                            <><ArrowRight className="w-3 h-3 mx-0.5" /> {s.to_location}</>
                          )}
                        </span>
                      )}
                      {s.expected_saving_meters != null && (
                        <span>{t('simulation.aiErsparnis')}: <strong className="text-[var(--text-primary)]">{s.expected_saving_meters}m</strong></span>
                      )}
                      {s.affected_picks_month != null && (
                        <span>{s.affected_picks_month} {t('simulation.aiPicksMonat')}</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
                  </div>
                </div>

                {/* Expanded actions */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-[var(--border-card)]">
                    <div className="flex items-center gap-2 pt-3">
                      {s.status === 'pending' && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); handleStatusChange(s.id, 'accepted'); }}
                            className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-lg hover:bg-emerald-500/20 transition-colors">
                            {t('simulation.aiAkzeptieren')}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleStatusChange(s.id, 'rejected'); }}
                            className="px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-medium rounded-lg hover:bg-red-500/20 transition-colors">
                            {t('simulation.aiAblehnen')}
                          </button>
                        </>
                      )}
                      {s.status === 'accepted' && (
                        <button onClick={(e) => { e.stopPropagation(); handleStatusChange(s.id, 'implemented'); }}
                          className="px-3 py-1.5 bg-[#338edc]/10 text-[#338edc] text-xs font-medium rounded-lg hover:bg-[#338edc]/20 transition-colors">
                          {t('simulation.aiUmgesetzt')}
                        </button>
                      )}
                      {(s.status === 'rejected' || s.status === 'implemented') && (
                        <button onClick={(e) => { e.stopPropagation(); handleStatusChange(s.id, 'pending'); }}
                          className="px-3 py-1.5 bg-[var(--bg-inset)] text-[var(--text-secondary)] text-xs font-medium rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors">
                          Reset
                        </button>
                      )}
                      {/* Show on floor plan button */}
                      <button onClick={(e) => { e.stopPropagation(); setShowFloorplan(showFloorplan === s.id ? null : s.id); }}
                        className="ml-auto px-3 py-1.5 bg-[var(--bg-inset)] text-[var(--text-secondary)] text-xs font-medium rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /> {t('simulation.aiZeigePlan')}
                      </button>
                    </div>

                    {/* Inline floor plan */}
                    {showFloorplan === s.id && config && (
                      <div className="mt-3 bg-[#0f172a] rounded-xl overflow-hidden" style={{ height: '220px' }}>
                        <SuggestionMiniMap
                          suggestion={s}
                          problemLines={problemLines}
                          config={config}
                        />
                      </div>
                    )}

                    {/* Timestamp */}
                    {s.created_at && (
                      <div className="mt-2 text-[11px] text-[var(--text-tertiary)]">
                        {t('simulation.aiGeneriert')}: {new Date(s.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Mini floor plan for suggestion visualization ───
function SuggestionMiniMap({ suggestion, problemLines, config }) {
  const w = config?.width || 30;
  const h = config?.height || 20;
  const scale = 30;
  const svgW = w * scale;
  const svgH = h * scale;

  // Find matching problem lines for this suggestion
  const relevantLines = problemLines.filter(pl =>
    (pl.location_a === suggestion.from_location || pl.location_b === suggestion.from_location ||
     pl.location_a === suggestion.to_location || pl.location_b === suggestion.to_location) &&
    pl.a && pl.b
  );

  // Show all problem lines faintly, relevant ones highlighted
  return (
    <svg width="100%" height="100%" viewBox={`-10 -5 ${svgW + 20} ${svgH + 10}`}
      preserveAspectRatio="xMidYMid meet" className="select-none">
      <rect x={0} y={0} width={svgW} height={svgH} fill="none" stroke="#475569" strokeWidth={1} rx={3} />

      {/* All problem lines (faint) */}
      {problemLines.filter(pl => pl.a && pl.b).map((pl, i) => {
        const isRelevant = relevantLines.includes(pl);
        return (
          <line key={i}
            x1={pl.a.x * scale} y1={pl.a.y * scale}
            x2={pl.b.x * scale} y2={pl.b.y * scale}
            stroke={isRelevant ? '#f59e0b' : '#64748b'}
            strokeWidth={isRelevant ? 2 : 0.5}
            strokeDasharray={isRelevant ? 'none' : '4 3'}
            opacity={isRelevant ? 0.8 : 0.2} />
        );
      })}

      {/* Problem line endpoints as small dots */}
      {problemLines.filter(pl => pl.a && pl.b).map((pl, i) => (
        <g key={`d-${i}`}>
          <circle cx={pl.a.x * scale} cy={pl.a.y * scale} r={2} fill="#64748b" opacity={0.3} />
          <circle cx={pl.b.x * scale} cy={pl.b.y * scale} r={2} fill="#64748b" opacity={0.3} />
        </g>
      ))}

      {/* Highlighted relevant endpoints */}
      {relevantLines.map((pl, i) => (
        <g key={`r-${i}`}>
          <circle cx={pl.a.x * scale} cy={pl.a.y * scale} r={5}
            fill="#f59e0b" fillOpacity={0.3} stroke="#f59e0b" strokeWidth={1.5} />
          <text x={pl.a.x * scale} y={pl.a.y * scale - 8}
            fill="#f59e0b" fontSize={8} fontWeight={600} textAnchor="middle">{pl.location_a}</text>
          <circle cx={pl.b.x * scale} cy={pl.b.y * scale} r={5}
            fill="#f59e0b" fillOpacity={0.3} stroke="#f59e0b" strokeWidth={1.5} />
          <text x={pl.b.x * scale} y={pl.b.y * scale - 8}
            fill="#f59e0b" fontSize={8} fontWeight={600} textAnchor="middle">{pl.location_b}</text>
        </g>
      ))}

      {/* No location data hint */}
      {relevantLines.length === 0 && (
        <text x={svgW / 2} y={svgH / 2} fill="#94a3b8" fontSize={11} textAnchor="middle">
          No position data for this suggestion
        </text>
      )}
    </svg>
  );
}


// ═════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════
export default function WarehouseSimulation() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('heatmap');

  // Unified time filter state
  const today = useMemo(() => getTodayVienna(), []);
  const [filterPreset, setFilterPreset] = useState(30);
  const [filterVon, setFilterVon] = useState(() => {
    const d = new Date(today + 'T12:00:00');
    d.setDate(d.getDate() - 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [filterBis, setFilterBis] = useState(today);

  function handleFilterChange({ preset, von, bis }) {
    setFilterPreset(preset);
    setFilterVon(von);
    setFilterBis(bis);
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('nav.warehouseSimulation')} subtitle={t('warehouse.subtitle')} />

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] inline-flex">
        <TabButton active={tab === 'heatmap'} onClick={() => setTab('heatmap')}>{t('simulation.heatmap')}</TabButton>
        <TabButton active={tab === 'kpis'} onClick={() => setTab('kpis')}>{t('simulation.kpis')}</TabButton>
        <TabButton active={tab === 'routen'} onClick={() => setTab('routen')}>{t('simulation.pickerRouten')}</TabButton>
        <TabButton active={tab === 'copick'} onClick={() => setTab('copick')}>{t('simulation.coPick')}</TabButton>
        <TabButton active={tab === 'ai'} onClick={() => setTab('ai')}>{t('simulation.aiOptimierung')}</TabButton>
      </div>

      {/* Unified time filter (not shown on AI tab) */}
      {tab !== 'ai' && (
        <SimulationTimeFilter preset={filterPreset} von={filterVon} bis={filterBis} onChange={handleFilterChange} t={t} />
      )}

      {tab === 'heatmap' && <HeatmapTab t={t} von={filterVon} bis={filterBis} />}
      {tab === 'kpis' && <KpisTab t={t} von={filterVon} bis={filterBis} />}
      {tab === 'routen' && <RoutenTab t={t} von={filterVon} bis={filterBis} />}
      {tab === 'copick' && <CopickTab t={t} von={filterVon} bis={filterBis} />}
      {tab === 'ai' && <AiOptimierungTab t={t} />}
    </div>
  );
}
